import { useCallback, useMemo, useState, useEffect } from 'react';
import _ from 'lodash';
import DataTable, { TableColumn, Alignment, SortOrder } from 'react-data-table-component';
import InfiniteScroll from 'react-infinite-scroll-component';
import { IoChevronDownCircle } from '@react-icons/all-files/io5/IoChevronDownCircle';
import PageErrorHandlerAndLoaderWrapper from 'Wrappers/PageErrorHandlerAndLoaderWrapper';
import SubHeaderComponent from './SubHeaderComponent';
import { DataTableProps, GridType } from 'Core-utils/types/types';
import CustomPagination from './CustomPagination';
import TableSubStates from './TableSubStates';
import { DEFAULT_TABLE_BODY_HEIGHT, FIXED_HEADER_SCROLL_HEIGHT } from 'Core-utils/constants/constants';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { getGridData, getApiData } from 'Apis/library';
import { extractQueryParameters, updateURLWithFiltersAndSorting } from 'Core-utils/helpers/helpers';
import { FilterCriteria } from '@ampsec/platform-client';
import './styles.scss';

const columnFilterMap: Record<string, string> = {
    provider: 'provider.displayValue',
};

const AMP_ENUMS: { [key: string]: string } = {
    ORGANIZATION: 'organization',
    DEPARTMENT: 'department',
    TITLE: 'position',
    SEVERITY: 'severity',
    [columnFilterMap.provider]: 'provider',
    CATEGORY: 'issue type',
};

const getFilterCriteria = (selectedOptions: { [key: string]: string[] }) => {
    return Object.keys(selectedOptions).reduce((prevSelection, currSelection) => {
        const cb = (value: string) => value === currSelection;
        const key = _.findKey(AMP_ENUMS, cb);
        if (key) {
            const formattedKey = key === 'provider.displayValue' ? key : key.toLowerCase();
            return { ...prevSelection, [formattedKey]: selectedOptions[currSelection] };
        }
        return prevSelection;
    }, {});
};

const customStyle = {
    subHeader: {
        style: {
            backgroundColor: '$structural-CardBG',
        },
    },
    table: {
        style: {
            minHeight: DEFAULT_TABLE_BODY_HEIGHT,
            backgroundColor: '$structural-CardBG',
        },
    },
};

function Table<T>({
    data,
    expandableRows = false,
    expandableRowsComponent,
    cacheKey,
    rowsPerPageOptions,
    tableBodyHeight,
    customStyles,
    columns,
    showSubHeader = true,
    defaultSortId,
    titleComponent,
    transformer,
    subHeaderVariant = 'filter-with-actions',
    subHeaderContent,
    filter,
    isMock = false,
    pagination = true,
    selectableRows,
    selectedRows,
    onSelectedRowsChange,
    clearSelectedRows,
    dropdownOptions = {},
    refreshTableData,
    dataIsLoading = false,
    updateUrl = false,
    onChangePage,
    onRowChange,
    currentPage,
    currentLimit,
    paginationTotalRows,
    isInfiniteScroll,
    onRowDoubleClicked,
    placeHolder,
    searchKey = 'q',
    operation,
    defaultSortOrder,
    componentIdentifier,
    noContextMenu = true,
}: DataTableProps<T>) {
    if (tableBodyHeight) {
        customStyle.table.style.minHeight = tableBodyHeight;
    }

    const [limit, setLimit] = useState(
        currentLimit ?? Number(extractQueryParameters()?.limit?.[0] ?? rowsPerPageOptions[0]),
    );
    const [page, setPage] = useState(currentPage ?? Number(extractQueryParameters()?.page?.[0] ?? 1));
    const [searchValue, setSearchValue] = useState<string>('');
    const [sortedColumnId, setSortedColumnId] = useState<TableColumn<T>['id']>(defaultSortId);
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(defaultSortOrder ?? 'ASC');

    const [selectedOptions, setSelectedOptions] = useState<{
        [key: string]: string[];
    }>(_.omit(extractQueryParameters(), 'page', 'limit'));
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 500);

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [searchValue]);

    const filters = useMemo(() => {
        const offset = (page - 1) * limit;
        const sort = sortOrder && sortedColumnId ? { [sortedColumnId]: sortOrder } : null;
        const search =
            debouncedSearchValue === ''
                ? null
                : {
                      [searchKey]: searchKey === 'q' ? debouncedSearchValue : { $like: `${debouncedSearchValue}*` },
                  };

        const updatedSelectedOptions = getFilterCriteria(selectedOptions);
        const updatedFilters = { offset, limit, ...updatedSelectedOptions };
        if (filter) {
            return { ...filter, ...updatedFilters, ...(sort ? { sort } : {}), ...search };
        }
        return sort ? { ...updatedFilters, sort, ...search } : { ...updatedFilters, ...search };
    }, [page, limit, sortOrder, sortedColumnId, selectedOptions, filter, debouncedSearchValue]);
    const onSortHandler = useCallback(
        (sortedColumn: TableColumn<T>, sortDirection: SortOrder) => {
            setSortOrder(sortDirection === 'asc' ? 'ASC' : 'DESC');
            setSortedColumnId(sortedColumn.id);
        },
        [columns],
    );

    // Updating the styles of currently sorted column and header
    const updatedColumns = useMemo(
        () =>
            columns.map((column) => {
                const tempColumn = { ...column };
                if (column.id === sortedColumnId) {
                    tempColumn.name = <div className="sorted-column w-100">{column.name}</div>;
                    tempColumn.conditionalCellStyles = [
                        {
                            when: (_row: T) => true,
                            classNames: ['sorted-column-cell'],
                        },
                    ];
                }
                return tempColumn;
            }),
        [sortedColumnId, columns],
    );

    const changePageHandler = (currentPage: number) => {
        setPage(currentPage);
        updateUrl && updateURLWithFiltersAndSorting({ page: currentPage, limit });
    };
    const changeRowsPerPageHandler = (rowsOption: number) => {
        setLimit(rowsOption);
        updateUrl && updateURLWithFiltersAndSorting({ limit: rowsOption, page });
    };

    function getGridDataWithOperations(inputUrl: GridType, filter?: FilterCriteria) {
        return operation ? getGridData(inputUrl, filter, operation) : getGridData(inputUrl, filter);
    }

    const { data: APIdata, isLoading, error, refresh } = data
        ? { data: { data }, isLoading: dataIsLoading, error: false, refresh: () => {} }
        : useApiObjectData(cacheKey, isMock ? getApiData : getGridDataWithOperations, transformer, filters);

    useEffect(() => {
        refresh();
    }, [refreshTableData]);

    const [asyncData, setAsyncData] = useState<T[]>(APIdata?.data ?? []);
    const handleInfiniteScroll = () => {
        setPage((prevPageNumber) => prevPageNumber + 1);
    };
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setSearchValue(inputValue);
    };

    const PaginationComponent = (props: any) => <CustomPagination {...props} rowsPerPageOptions={rowsPerPageOptions} />;

    useEffect(() => {
        if (APIdata?.data) {
            setAsyncData((prevAsyncData) => [...prevAsyncData, ...APIdata?.data]);
        }
    }, [APIdata?.data]);

    const TableComponent = () => (
        <div id="table-skeleton" className="d-flex flex-column p-0 table-skeleton">
            {!isInfiniteScroll ? (
                <DataTable
                    data={APIdata?.data}
                    expandableRows={expandableRows}
                    expandableRowsComponent={expandableRowsComponent}
                    columns={updatedColumns}
                    subHeader={showSubHeader}
                    subHeaderComponent={
                        <SubHeaderComponent
                            columns={columns}
                            subHeaderVariant={subHeaderVariant}
                            selectedRows={selectedRows}
                            dropdownOptions={dropdownOptions}
                            setSelectedOptions={setSelectedOptions}
                            subHeaderContent={subHeaderContent}
                            selectedOptions={selectedOptions}
                            searchInputValue={searchValue}
                            onSearchChange={handleSearchChange}
                            filterColumns={Object.keys(dropdownOptions)}
                            placeHolder={placeHolder}
                        />
                    }
                    subHeaderAlign={Alignment.LEFT}
                    className="custom-scrollbar"
                    pagination={pagination}
                    paginationServer
                    paginationComponent={PaginationComponent}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={rowsPerPageOptions}
                    paginationDefaultPage={page}
                    onChangePage={onChangePage ?? changePageHandler}
                    onChangeRowsPerPage={onRowChange ?? changeRowsPerPageHandler}
                    sortIcon={<IoChevronDownCircle />}
                    defaultSortFieldId={defaultSortId}
                    fixedHeader
                    persistTableHead={true}
                    title={titleComponent}
                    fixedHeaderScrollHeight={tableBodyHeight ?? FIXED_HEADER_SCROLL_HEIGHT}
                    customStyles={customStyles ?? customStyle}
                    progressPending={isLoading}
                    progressComponent={<TableSubStates variant="loader" />}
                    paginationTotalRows={paginationTotalRows ?? APIdata?.hints?.count}
                    noDataComponent={<TableSubStates variant="empty" error={error} />}
                    onSort={onSortHandler}
                    selectableRows={selectableRows ?? false}
                    onSelectedRowsChange={onSelectedRowsChange}
                    clearSelectedRows={clearSelectedRows}
                    onRowDoubleClicked={onRowDoubleClicked}
                    noContextMenu={noContextMenu}
                />
            ) : (
                <InfiniteScroll
                    dataLength={APIdata?.data.length ?? 0}
                    next={handleInfiniteScroll}
                    hasMore={APIdata?.hints?.hasMore ?? false}
                    loader={<TableSubStates variant="loader" />}
                    className="custom-scrollbar bg-structural-CardBG"
                >
                    <DataTable
                        data={asyncData}
                        columns={updatedColumns}
                        subHeader={showSubHeader}
                        subHeaderComponent={
                            <SubHeaderComponent
                                columns={columns}
                                subHeaderVariant={subHeaderVariant}
                                selectedRows={selectedRows}
                                dropdownOptions={dropdownOptions}
                                setSelectedOptions={setSelectedOptions}
                                subHeaderContent={subHeaderContent}
                                selectedOptions={selectedOptions}
                                searchInputValue={searchValue}
                                onSearchChange={handleSearchChange}
                                filterColumns={Object.keys(dropdownOptions)}
                            />
                        }
                        subHeaderAlign={Alignment.LEFT}
                        className="custom-scrollbar custom-table"
                        pagination={pagination}
                        paginationServer
                        paginationComponent={(props) => (
                            <CustomPagination {...props} rowsPerPageOptions={rowsPerPageOptions} />
                        )}
                        paginationPerPage={limit}
                        paginationDefaultPage={page}
                        paginationRowsPerPageOptions={rowsPerPageOptions}
                        onChangePage={changePageHandler}
                        onChangeRowsPerPage={changeRowsPerPageHandler}
                        sortIcon={<IoChevronDownCircle />}
                        defaultSortFieldId={defaultSortId}
                        fixedHeader
                        persistTableHead={true}
                        title={titleComponent}
                        fixedHeaderScrollHeight={tableBodyHeight}
                        customStyles={customStyle}
                        progressPending={isLoading}
                        progressComponent={<TableSubStates variant="loader" />}
                        paginationTotalRows={APIdata?.hints?.count}
                        noDataComponent={<TableSubStates variant="empty" />}
                        onSort={onSortHandler}
                        selectableRows={selectableRows ?? false}
                        onSelectedRowsChange={onSelectedRowsChange}
                        noContextMenu={noContextMenu}
                    />
                </InfiniteScroll>
            )}
        </div>
    );
    return <PageErrorHandlerAndLoaderWrapper error={error} render={TableComponent} component={componentIdentifier} />;
}

export default Table;
